import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "types";
import {
  MOCK_ADD_ON_FORM_VALUES,
  MOCK_ADD_ON_TABLES,
  MOCK_ADDITIONAL_EQUIPMENT,
  MOCK_BOX_FORM_VALUES,
  MOCK_CENTRE_OF_GRAVITY_FORM,
  MOCK_CHASSIS_LOADS,
  MOCK_DIAGRAM_PAGES,
  MOCK_LOAD_DISTRIBUTION_FORM_VALUES,
  buildLoadDistributionPdfData,
  MOCK_QUOTATION_BOX_STRUCTURE,
  MOCK_QUOTATION_FORM_VALUES,
  MOCK_QUOTATION_LOCKS,
  MOCK_QUOTATION_ROOF_BOX_STRUCTURE,
  MOCK_QUOTATION_CLOSED_BOX_STRUCTURE,
  MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES,
  MOCK_QUOTATION_INSULATED_BOX_STRUCTURE,
  MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES,
  MOCK_QUOTATION_ROOF_BOX_ACCESSORIES,
  MOCK_QUOTATION_SAFETY_VEHICLE,
  MOCK_QUOTATION_STANDARD_95,
  MOCK_QUOTATION_TYING_EQUIPMENT,
  MOCK_VEHICLE_FORM_VALUES,
  MOCK_VEHICLE_MEASURES,
  MOCK_WEIGHT_AND_WALL_FORM_VALUES,
} from "../components/WorkOrderDetails/data";
import { WorkOrderStatusFilter } from "../types";
import { initialState } from "./initialState";

const selectDomain = (state?: RootState) =>
  state?.workOrderState || initialState;

export const selectWorkOrderLoading = createSelector(
  [selectDomain],
  (state) => state.loading
);

export const selectWorkOrderDetailLoading = createSelector(
  [selectDomain],
  (state) => state.detailLoading
);

export const selectWorkOrderError = createSelector(
  [selectDomain],
  (state) => state.error
);

export const selectStatusFilter = createSelector(
  [selectDomain],
  (state) => state.statusFilter
);

export const selectWorkOrders = createSelector(
  [selectDomain],
  (state) => state.workOrders
);

export const selectFilteredWorkOrders = createSelector(
  [selectDomain],
  (state) => {
    const { workOrders, statusFilter } = state;

    if (statusFilter.includes("ALL")) {
      return workOrders;
    }

    return workOrders.filter((workOrder) =>
      statusFilter.includes(workOrder.status)
    );
  }
);

export const selectWorkOrderStatusLabelKey = (
  status: WorkOrderStatusFilter
) =>
  status === "ALL"
    ? "WORK_ORDER.STATUS_ALL"
    : `WORK_ORDER.STATUS_VALUES.${status}`;

export const selectSelectedWorkOrderId = createSelector(
  [selectDomain],
  (state) => state.selectedWorkOrderId
);

export const selectWorkOrderVehicleDetails = createSelector(
  [selectDomain],
  (state) => state.vehicleDetails
);

export const selectVehicleDetailsForm = createSelector(
  [selectDomain],
  (state) => state.vehicleDetailsForm ?? MOCK_VEHICLE_FORM_VALUES
);

export const selectBoxDetailsForm = createSelector(
  [selectDomain],
  (state) => state.boxDetailsForm ?? MOCK_BOX_FORM_VALUES
);

export const selectWeightAndWallForm = createSelector(
  [selectDomain],
  (state) => state.weightAndWallForm ?? MOCK_WEIGHT_AND_WALL_FORM_VALUES
);

export const selectLoadDistributionForm = createSelector(
  [selectDomain],
  (state) => state.loadDistributionForm ?? MOCK_LOAD_DISTRIBUTION_FORM_VALUES
);

export const selectLoadDistributionPdfData = createSelector(
  [selectLoadDistributionForm],
  (form) => buildLoadDistributionPdfData(form)
);

export const selectQuotationForm = createSelector(
  [selectDomain],
  (state) => state.quotationForm ?? MOCK_QUOTATION_FORM_VALUES
);

export const selectQuotationBoxStructure = createSelector(
  [selectDomain],
  (state) => state.quotationBoxStructure ?? MOCK_QUOTATION_BOX_STRUCTURE
);

export const selectQuotationRoofBoxStructure = createSelector(
  [selectDomain],
  (state) =>
    state.quotationRoofBoxStructure ?? MOCK_QUOTATION_ROOF_BOX_STRUCTURE
);

export const selectQuotationClosedBoxStructure = createSelector(
  [selectDomain],
  (state) =>
    state.quotationClosedBoxStructure ?? MOCK_QUOTATION_CLOSED_BOX_STRUCTURE
);

export const selectQuotationClosedBoxAccessories = createSelector(
  [selectDomain],
  (state) =>
    state.quotationClosedBoxAccessories ?? MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES
);

export const selectQuotationInsulatedBoxStructure = createSelector(
  [selectDomain],
  (state) =>
    state.quotationInsulatedBoxStructure ??
    MOCK_QUOTATION_INSULATED_BOX_STRUCTURE
);

export const selectQuotationInsulatedBoxAccessories = createSelector(
  [selectDomain],
  (state) =>
    state.quotationInsulatedBoxAccessories ??
    MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES
);

export const selectQuotationRoofBoxAccessories = createSelector(
  [selectDomain],
  (state) =>
    state.quotationRoofBoxAccessories ?? MOCK_QUOTATION_ROOF_BOX_ACCESSORIES
);

export const selectQuotationTyingEquipment = createSelector(
  [selectDomain],
  (state) => state.quotationTyingEquipment ?? MOCK_QUOTATION_TYING_EQUIPMENT
);

export const selectQuotationSafetyVehicle = createSelector(
  [selectDomain],
  (state) => state.quotationSafetyVehicle ?? MOCK_QUOTATION_SAFETY_VEHICLE
);

export const selectQuotationLocks = createSelector(
  [selectDomain],
  (state) => state.quotationLocks ?? MOCK_QUOTATION_LOCKS
);

export const selectQuotationStandard95 = createSelector(
  [selectDomain],
  (state) => state.quotationStandard95 ?? MOCK_QUOTATION_STANDARD_95
);

export const selectCentreOfGravityForm = createSelector(
  [selectDomain],
  (state) => state.centreOfGravityForm ?? MOCK_CENTRE_OF_GRAVITY_FORM
);

export const selectChassisLoads = createSelector(
  [selectDomain],
  (state) => state.chassisLoads ?? MOCK_CHASSIS_LOADS
);

export const selectVehicleMeasures = createSelector(
  [selectDomain],
  (state) => state.vehicleMeasures ?? MOCK_VEHICLE_MEASURES
);

export const selectAdditionalEquipment = createSelector(
  [selectDomain],
  (state) => state.additionalEquipment ?? MOCK_ADDITIONAL_EQUIPMENT
);

const defaultAddOnTyingStripModal = {
  open: false,
  titleKey: "",
  height: "",
  note: "",
};

const defaultRowModal = {
  open: false,
  tableKey: null,
  titleKey: "",
  location: "",
  itemType: "",
  quantity: "1",
  note: "",
};

export const selectAddOnDetails = createSelector([selectDomain], (state) =>
  state.addOnDetails ?? {
    form: MOCK_ADD_ON_FORM_VALUES,
    tables: MOCK_ADD_ON_TABLES,
    tyingStripModal: defaultAddOnTyingStripModal,
    rowModal: defaultRowModal,
  }
);

export const selectAddOnForm = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.form
);

export const selectAddOnTables = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.tables
);

export const selectAddOnTyingStripModal = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.tyingStripModal
);

export const selectRowModal = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.rowModal
);

export const selectAddOnTyingStripModalOpen = createSelector(
  [selectAddOnTyingStripModal],
  (tyingStripModal) => tyingStripModal?.open ?? false
);

export const selectDiagramView = createSelector(
  [selectDomain],
  (state) => state.diagramView
);

export const selectDiagramActiveDepartment = createSelector(
  [selectDiagramView],
  (diagramView) => diagramView.activeDepartment
);

export const selectDiagramActivePage = createSelector(
  [selectDiagramView],
  (diagramView) => diagramView.activePage
);

export const selectDiagramPages = createSelector(
  [selectDiagramView],
  (diagramView) => (diagramView.pages.length > 0 ? diagramView.pages : MOCK_DIAGRAM_PAGES)
);

export const selectActiveDiagramPageData = createSelector(
  [selectDiagramPages, selectDiagramActivePage],
  (pages, activePage) => pages[activePage] ?? pages[0] ?? null
);

export const selectActiveDetailTab = createSelector(
  [selectDomain],
  (state) => state.activeDetailTab
);

export const selectWorkOrderById = (state: RootState, workOrderId: number) => {
  const { workOrders, selectedWorkOrderId, vehicleDetails } =
    selectDomain(state);

  if (selectedWorkOrderId === workOrderId && vehicleDetails) {
    return vehicleDetails;
  }

  return null;
};
